/**
 * Everything the tracker reads and writes goes through the API.
 *
 * Unlike the rest of db-admin, this feature never touches Firestore directly. Two reasons:
 * the audit trail has to be stamped with an actor the client cannot forge, and the Firestore
 * security rules are not in this repo, so a direct-read page would need someone to hand-edit
 * rules in the console before it worked at all.
 */
import { createApiCall } from '../../../services/apiClient';
import type {
  HipaaActivityEntry,
  HipaaComment,
  HipaaItem,
  HipaaItemPatch,
} from '../types/hipaaTypes';

interface GetChecklistResponse {
  items: HipaaItem[];
  activity: HipaaActivityEntry[];
}

interface GetItemDetailResponse {
  comments: HipaaComment[];
  activity: HipaaActivityEntry[];
}

interface UpdateItemResponse {
  item: HipaaItem;
}

interface AddCommentResponse {
  comment: HipaaComment;
  commentCount: number;
}

const getChecklistCall = createApiCall<void, GetChecklistResponse>('adminHipaaGetChecklist');
const getItemDetailCall = createApiCall<{ itemId: string }, GetItemDetailResponse>(
  'adminHipaaGetItemDetail',
);
const updateItemCall = createApiCall<{ itemId: string; patch: HipaaItemPatch }, UpdateItemResponse>(
  'adminHipaaUpdateItem',
);
const addCommentCall = createApiCall<{ itemId: string; text: string }, AddCommentResponse>(
  'adminHipaaAddComment',
);

class HipaaService {
  private static instance: HipaaService;

  private constructor() {}

  public static getInstance(): HipaaService {
    if (!HipaaService.instance) {
      HipaaService.instance = new HipaaService();
    }
    return HipaaService.instance;
  }

  /** Every item plus recent activity, in one round trip. */
  public async getChecklist(): Promise<GetChecklistResponse> {
    return getChecklistCall(undefined);
  }

  public async getItemDetail(itemId: string): Promise<GetItemDetailResponse> {
    return getItemDetailCall({ itemId });
  }

  /** Returns the item as written, so the caller can patch its list in place. */
  public async updateItem(itemId: string, patch: HipaaItemPatch): Promise<HipaaItem> {
    const { item } = await updateItemCall({ itemId, patch });
    return item;
  }

  public async addComment(itemId: string, text: string): Promise<AddCommentResponse> {
    return addCommentCall({ itemId, text });
  }
}

export const hipaaService = HipaaService.getInstance();
